import React, { useEffect, useState } from "react";
import { Calendar, ChevronRight } from "lucide-react";
import MarkdownText from "../common/MarkdownText";
import DropPointsButton from "../events/DropPointsButton";
import JoinWhatsappGroupLink from "../events/JoinWhatsappGroupLink";
import RequestPickupButton from "../events/RequestPickupButton";
import UpcomingEventCard from "../events/UpcomingEventCard";
import PastEventDescription from "../events/PastEventDescription";
import PastEventThumbnail from "../events/PastEventThumbnail";
import { upcomingEvents, pastEvents } from "../../data/eventsData";
import {
  eventPickupEnabled,
  fetchPublicEvents,
  publicDropPoints,
  publicWhatsappGroupUrl,
  selectPastEvents,
} from "../../utils/publicEventsApi";
import { useGalleryViewer } from "../gallery/GalleryViewerProvider";
/** Past-card actions keep the filled pair layout. */
const actionSlotClass =
  "min-h-11 min-w-[min(100%,8.75rem)] grow shrink basis-[calc(50%-0.25rem)] gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-center text-sm font-semibold transition duration-[220ms] active:scale-[0.98] flex items-center justify-center";
const primaryActionClass =
  actionSlotClass +
  " bg-[var(--smym-accent)] text-[oklch(0.99_0.01_78)] hover:bg-[var(--smym-accent-hover)]";
function pastInfoAction(event, onViewFlyer, desktop = false) {
  if (event.detailsPath && event.detailsLabel?.toLowerCase() === "view scores")
    return null;
  const className = `smym-event-card__link smym-past-details-link${desktop ? " smym-past-desktop-action" : ""}`;
  const chevron = React.createElement(ChevronRight, {
    className: "h-3.5 w-3.5 shrink-0",
    "aria-hidden": true,
  });
  if (event.detailsPath)
    return React.createElement(
      "a",
      { href: event.detailsPath, className },
      event.detailsLabel || "View Details",
      chevron,
    );
  if (hasFlyer(event))
    return React.createElement(
      "button",
      { type: "button", onClick: () => onViewFlyer(event), className },
      "View Details",
      chevron,
    );
  return null;
}

const IST = "Asia/Kolkata";
/** View Details opens the flyer. Hide it when the event has no image. */
const hasFlyer = (event) =>
  typeof event?.flyer === "string" && event.flyer.trim() !== "";
/** Bulandi stays on the site even when the public feed hides members-only events. */
const pinnedPastEvents = pastEvents.filter((event) => event.detailsPath);
const STATIC_EVENTS = { upcoming: upcomingEvents, past: pastEvents, source: "static" };

/** API events when reachable; otherwise the bundled list so the section never goes blank. */
function usePublicEvents() {
  const [state, setState] = useState({ upcoming: [], past: [], source: "loading" });
  useEffect(() => {
    const ctrl = new AbortController();
    fetchPublicEvents({ signal: ctrl.signal })
      .then(({ upcoming, past }) => {
        setState({
          upcoming,
          past: selectPastEvents(past, pastEvents, pinnedPastEvents),
          source: "api",
        });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState(STATIC_EVENTS);
      });
    return () => ctrl.abort();
  }, []);
  return state;
}

const loadingCards = (className) =>
  [0, 1, 2].map((i) =>
    React.createElement("div", {
      key: `loading-${i}`,
      className: `${className} smym-event-card--loading`,
      "aria-hidden": true,
    }),
  );

const PAST_PREVIEW_COUNT = 3;

const EventsSection = ({ onViewFlyer, onRegisterEvent, onViewPhotos }) => {
  const { upcoming, past, source } = usePublicEvents();
  const { openAlbumSlug } = useGalleryViewer();
  const [showAllPast, setShowAllPast] = useState(false);
  const visiblePast = showAllPast ? past : past.slice(0, PAST_PREVIEW_COUNT);
  const loading = source === "loading";
  return React.createElement(
    "section",
    {
      id: "events",
      className: "smym-section border-t border-[var(--smym-line)] px-4 sm:px-6",
    },
    React.createElement(
      "div",
      { className: "max-w-7xl mx-auto" },
      React.createElement(
        "div",
        { className: "text-center mb-12" },
        React.createElement(
          "h2",
          { className: "smym-section-title text-center mb-4" },
          "Events",
        ),
        React.createElement(
          "p",
          {
            className:
              "text-center text-[var(--smym-ink-muted)] mx-auto [max-width:42ch] mb-2",
          },
          "Upcoming events and highlights from the past year",
        ),
      ),
      React.createElement(
        "div",
        { className: "mb-12" },
        React.createElement(
          "h3",
          {
            className:
              "mb-6 flex items-center text-2xl font-bold text-[var(--smym-ink)]",
          },
          React.createElement(Calendar, {
            className: "mr-2 text-[var(--smym-accent)]",
          }),
          "Upcoming Events",
        ),
        React.createElement(
          "div",
          {
            className:
              "grid items-stretch gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3",
            "aria-busy": loading ? "true" : undefined,
          },
          loading
            ? loadingCards("smym-event-card smym-event-card--upcoming")
            : upcoming.length === 0
              ? React.createElement(
                  "p",
                  { className: "text-[var(--smym-ink-muted)]" },
                  "New events will be announced soon.",
                )
              : null,
          upcoming.map((event) =>
            React.createElement(UpcomingEventCard, {
              key: event.id,
              event,
              onViewFlyer,
              onRegisterEvent,
            }),
          ),
        ),
      ),
      React.createElement(
        "div",
        null,
        React.createElement(
          "h3",
          {
            className:
              "mb-6 flex items-center text-2xl font-bold text-[var(--smym-ink)]",
          },
          React.createElement(Calendar, {
            className: "mr-2 text-[var(--smym-accent)]",
          }),
          "Past Events in 12 months",
        ),
        React.createElement(
          "div",
          {
            className: "grid items-start gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3",
            "aria-busy": loading ? "true" : undefined,
          },
          loading ? loadingCards("smym-event-card smym-event-card--past") : null,
          visiblePast.map((event, idx) =>
            React.createElement(
              "div",
              {
                key: event.id ?? `${event.name}-${idx}`,
                className: "smym-event-card smym-event-card--past p-4",
              },
              React.createElement(
                "div",
                { className: "smym-past-event-heading" },
                React.createElement(PastEventThumbnail, { event }),
                React.createElement(
                  "h4",
                  {
                    className: "mb-2 font-bold text-[var(--smym-ink)]",
                  },
                  event.name,
                ),
                React.createElement(
                  "p",
                  {
                    className: "smym-event-card__date--past mb-2 text-xs",
                  },
                  new Date(event.date).toLocaleDateString("en-IN", {
                    timeZone: IST,
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  }),
                ),
              ),
              React.createElement(PastEventDescription, {
                description: event.description,
                attendees: event.attendees,
                coordinator: event.coordinator,
                specialMention: event.specialMention,
                detailsAction: pastInfoAction(event, onViewFlyer),
              }),
              event.attendees != null &&
                React.createElement(
                  "div",
                  {
                    className:
                      "smym-past-desktop-metadata flex items-center justify-between text-sm",
                  },
                  React.createElement(
                    "span",
                    {
                      className: "font-semibold text-[var(--smym-accent)]",
                    },
                    event.attendees,
                    " Participants",
                  ),
                ),
              event.detailsPath && event.detailsLabel?.toLowerCase() === "view scores"
                ? React.createElement(
                    "a",
                    {
                      href: event.detailsPath,
                      className: `mt-3 ${primaryActionClass}`,
                    },
                    event.detailsLabel,
                    React.createElement(ChevronRight, {
                      className: "h-3.5 w-3.5 shrink-0",
                      "aria-hidden": true,
                    }),
                  )
                : pastInfoAction(event, onViewFlyer, true),
              publicWhatsappGroupUrl(event)
                ? React.createElement(JoinWhatsappGroupLink, {
                    event,
                    className: "smym-event-card__link",
                  })
                : null,
              event.galleryAlbumSlug ||
                publicDropPoints(event).length > 0 ||
                eventPickupEnabled(event)
                ? React.createElement(
                    "div",
                    { className: "mt-3 flex flex-wrap gap-2" },
                    event.galleryAlbumSlug
                      ? React.createElement(
                          "button",
                          {
                            type: "button",
                            className: primaryActionClass,
                            onClick: () => {
                              if (onViewPhotos) onViewPhotos(event);
                              else openAlbumSlug(event.galleryAlbumSlug);
                            },
                          },
                          "View photos",
                        )
                      : null,
                    publicDropPoints(event).length > 0
                      ? React.createElement(DropPointsButton, {
                          event,
                          className: primaryActionClass,
                        })
                      : null,
                    eventPickupEnabled(event)
                      ? React.createElement(RequestPickupButton, {
                          event,
                          className: primaryActionClass,
                        })
                      : null,
                  )
                : null,
              event.coordinator &&
                React.createElement(
                  "p",
                  {
                    className:
                      "smym-past-desktop-metadata mt-2 text-xs text-[var(--smym-ink-muted)]",
                  },
                  React.createElement(
                    "span",
                    { className: "font-medium" },
                    "Coordinator:",
                  ),
                  " ",
                  event.coordinator,
                ),
              event.specialMention &&
                React.createElement(
                  "div",
                  {
                    className:
                      "smym-past-desktop-metadata mt-1 text-xs text-[var(--smym-accent)]",
                  },
                  React.createElement(
                    "span",
                    { className: "font-medium" },
                    "Special Mention:",
                  ),
                  " ",
                  React.createElement(MarkdownText, {
                    inline: true,
                    children: event.specialMention,
                  }),
                ),
            ),
          ),
        ),
        !loading && past.length > PAST_PREVIEW_COUNT
          ? React.createElement(
              "div",
              { className: "mt-6 text-center" },
              React.createElement(
                "button",
                {
                  type: "button",
                  onClick: () => setShowAllPast((v) => !v),
                  "aria-expanded": showAllPast,
                  className: "smym-btn smym-btn-outline",
                },
                showAllPast ? "Show fewer past events" : "Show all past events",
              ),
            )
          : null,
      ),
    ),
  );
};
export default EventsSection;
